import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ItemVersionOrigin } from "../../../api/content/authoring/itemUsageApi";
import { readAuthoredItem } from "../../../api/content/authoring/processAuthoringApi";
import { appPaths } from "../../../shared/routing/appPaths";
import { useLoad } from "../../../shared/loading/useLoad";
import AppButton from "../../../ui/buttons/AppButton";
import SourceDiff from "../../../ui/content/source/SourceDiff";
import FieldChangeList from "../changes/FieldChangeList";
import { fieldDiff } from "../../../shared/diff/fieldDiff";
import { shortReference } from "../../../shared/references/shortReferences";
import MadeByText from "./MadeByText";
import { CONTROL_ROW_TEXT_CLASS } from "../../processAuthoring/shared/authoringStyles";
import Chip from "../../../ui/feedback/Chip";
import EmptyState from "../../../ui/feedback/EmptyState";
import LoadStatus from "../../../ui/feedback/LoadStatus";

/** Every version `main` held, newest first, each with who or what made it and its diff to the one before (AU-5). */
export default function ArtifactVersions({ versions, shown }: { versions: readonly ItemVersionOrigin[]; shown: string }) {
  const { t } = useTranslation();
  return (
    <section className="app-card space-y-2 p-3">
      <h3 className="app-title text-base font-semibold">{t("authoring.artifactPage.versions")}</h3>
      {versions.length === 0 ? <EmptyState>{t("authoring.artifactPage.noVersions")}</EmptyState> : null}
      <ol className="space-y-2">
        {versions.map((version, index) => (
          <VersionEntry key={version.reference} version={version} number={versions.length - index} previous={versions[index + 1]?.reference ?? null} shown={version.reference === shown} />
        ))}
      </ol>
    </section>
  );
}

function VersionEntry({ version, number, previous, shown }: { version: ItemVersionOrigin; number: number; previous: string | null; shown: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <li className="space-y-1 border-l-2 pl-3">
      <p className={CONTROL_ROW_TEXT_CLASS}>
        <span className="font-semibold">{t("authoring.artifactPage.version", { number })}</span>
        <Link to={appPaths.item(version.reference)} className="app-link font-mono text-xs underline" aria-current={shown ? "page" : undefined}>
          @{shortReference("version", version.reference)}
        </Link>
        {shown ? <Chip>{t("authoring.artifactPage.shown")}</Chip> : null}
        {previous === null ? null : (
          <AppButton variant="ghost" size="xs" aria-expanded={open} onClick={() => { setOpen(!open); }}>
            {open ? t("authoring.artifactPage.hideDiff") : t("authoring.artifactPage.showDiff")}
          </AppButton>
        )}
      </p>
      <MadeByText madeBy={version.madeBy} />
      {open && previous !== null ? <VersionDiff before={previous} after={version.reference} /> : null}
    </li>
  );
}

/** JSON content field by field; Markdown line by line. */
function VersionDiff({ before, after }: { before: string; after: string }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => Promise.all([readAuthoredItem(before, signal), readAuthoredItem(after, signal)]), [before, after]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} compact />;
  const [old, next] = state.value;
  const oldContent = old.content?.kind === "artifact" ? old.content.definition.content : null;
  const newContent = next.content?.kind === "artifact" ? next.content.definition.content : null;
  if (typeof oldContent === "string" && typeof newContent === "string") {
    return <SourceDiff label={t("authoring.artifactPage.diffLabel")} language="markdown" original={oldContent} modified={newContent} />;
  }
  return <FieldChangeList changes={fieldDiff(oldContent, newContent)} />;
}
